import React, {useState} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import './App.css';

const {FiUser,FiMail,FiPhone,FiCalendar,FiSend,FiCheck,FiAlertCircle,FiInfo,FiMapPin}=FiIcons;

function App() {
  const [formData,setFormData]=useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    availableDates: []
  });
  
  const [isSubmitting,setIsSubmitting]=useState(false);
  const [submitStatus,setSubmitStatus]=useState(null);

  // Generate specific Saturday dates for 2025 season with manual formatting
  const generateSaturdays=()=> {
    const saturdays=[
      {
        date: '2025-09-06',
        display: 'Saturday, September 6, 2025'
      },
      {
        date: '2025-09-13', 
        display: 'Saturday, September 13, 2025'
      },
      {
        date: '2025-09-20',
        display: 'Saturday, September 20, 2025'
      },
      {
        date: '2025-09-27',
        display: 'Saturday, September 27, 2025'
      },
      {
        date: '2025-10-04',
        display: 'Saturday, October 4, 2025'
      }
    ];

    return saturdays;
  };

  const saturdays=generateSaturdays();

  const handleInputChange=(e)=> {
    const {name,value}=e.target;
    setFormData(prev=> ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateToggle=(date)=> {
    setFormData(prev=> ({
      ...prev,
      availableDates: prev.availableDates.includes(date) 
        ? prev.availableDates.filter(d=> d !==date) 
        : [...prev.availableDates,date]
    }));
  };

  const handleSubmit=async (e)=> {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response=await fetch('https://cloud.activepieces.com/api/v1/webhooks/JQEgvKpTGnI0mTc3gC6ql',{
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          submittedAt: new Date().toISOString(),
          program: 'Challenger Baseball',
          timeSlot: '1:30 PM - 2:30 PM',
          location: 'High Road Field, Corner of High Road and Carmel, Fall River',
          season: '2025'
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          availableDates: []
        });
      } else {
        throw new Error('Failed to submit form');
      }
    } catch (error) {
      console.error('Error submitting form:',error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid=formData.firstName && formData.lastName && formData.email && formData.phone && formData.availableDates.length > 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-yellow-50 py-8 px-4 baseball-bg">
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          transition={{duration: 0.6}}
          className="bg-white rounded-2xl shadow-2xl overflow-hidden border-4 border-green-600"
        >
          {/* Header with Baseball Team Image */}
          <div className="relative overflow-hidden">
            {/* Background Image */}
            <div 
              className="h-64 bg-cover bg-center relative"
              style={{
                backgroundImage: `url('https://scontent.fyhz1-1.fna.fbcdn.net/v/t39.30808-6/534148622_10163910614695476_2356148345635846040_n.jpg?stp=cp6_dst-jpegr_tt6&_nc_cat=110&ccb=1-7&_nc_sid=86c6b0&_nc_ohc=lr2GnE0YDuEQ7kNvwFigbyz&_nc_oc=Adliy9BKhGK_JjiH7AOcaxE-5agIQCJ0-g9MserB-LP5hTAtOds52FTKds7lNauNZRw&_nc_zt=23&se=-1&_nc_ht=scontent.fyhz1-1.fna&_nc_gid=L3N52OXOd9lyAVDebUAxMQ&oh=00_AfWmJ8Y5Mz1HZvY6UarTmfDKcKAFcG3LD5L77NzKItdOFA&oe=68B096CD')`
              }}
            >
              {/* Overlay for better text readability */}
              <div className="absolute inset-0 bg-gradient-to-r from-green-900/80 via-green-800/70 to-yellow-600/60"></div>
            </div>

            {/* Content overlay */}
            <div className="absolute inset-0 flex items-center justify-center p-8">
              <div className="text-center text-white z-10">
                <div className="flex items-center justify-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-2xl">⚾</span>
                  </div>
                  <h1 className="text-4xl font-bold drop-shadow-lg">Challenger Baseball</h1>
                </div>
                <h2 className="text-2xl font-semibold mb-3 drop-shadow-lg">2025 Volunteer Sign-Up</h2>
                <p className="text-lg text-green-100 drop-shadow-md max-w-md mx-auto">
                  Join our team every Saturday (1:30 - 2:30 PM) and help make baseball accessible for everyone!
                </p>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            {/* Personal Information */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                <SafeIcon icon={FiUser} className="text-green-600" />
                Personal Information
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                    placeholder="Enter your first name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                    placeholder="Enter your last name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <SafeIcon icon={FiMail} className="inline mr-1" />
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                  placeholder="Enter your email address"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <SafeIcon icon={FiPhone} className="inline mr-1" />
                  Phone Number *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                  placeholder="Enter your phone number"
                />
              </div>
            </div>

            {/* Available Dates */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                <SafeIcon icon={FiCalendar} className="text-yellow-600" />
                Available Saturdays * (Fall 2025 Season)
              </h2>
              <p className="text-sm text-gray-600">Select all Saturdays you can volunteer (1:30 - 2:30 PM)</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {saturdays.map((saturday)=> (
                  <motion.div
                    key={saturday.date}
                    whileHover={{scale: 1.02}}
                    whileTap={{scale: 0.98}}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all relative overflow-hidden ${
                      formData.availableDates.includes(saturday.date)
                        ? 'border-green-500 bg-green-50 text-green-800 shadow-lg'
                        : 'border-gray-300 hover:border-green-300 hover:bg-green-50'
                    }`}
                    onClick={()=> handleDateToggle(saturday.date)}
                  >
                    {formData.availableDates.includes(saturday.date) && (
                      <div className="absolute top-2 right-2">
                        <span className="text-lg">⚾</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{saturday.display}</span>
                      {formData.availableDates.includes(saturday.date) && (
                        <SafeIcon icon={FiCheck} className="text-green-600" />
                      )}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">1:30 - 2:30 PM</div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Submit Status */}
            {submitStatus==='success' && (
              <motion.div
                initial={{opacity: 0,y: 10}}
                animate={{opacity: 1,y: 0}}
                className="p-4 bg-green-50 border-2 border-green-200 rounded-lg flex items-center gap-2 text-green-800"
              >
                <SafeIcon icon={FiCheck} className="text-green-600" />
                <span className="mr-2">⚾</span>
                Thank you for joining our Fall 2025 team! We'll be in touch with more details.
              </motion.div>
            )}

            {submitStatus==='error' && (
              <motion.div
                initial={{opacity: 0,y: 10}}
                animate={{opacity: 1,y: 0}}
                className="p-4 bg-red-50 border-2 border-red-200 rounded-lg flex items-center gap-2 text-red-800"
              >
                <SafeIcon icon={FiAlertCircle} className="text-red-600" />
                There was an error submitting your form. Please try again.
              </motion.div>
            )}

            {/* Submit Button */}
            <motion.button
              type="submit"
              disabled={!isFormValid || isSubmitting}
              whileHover={{scale: isFormValid ? 1.02 : 1}}
              whileTap={{scale: isFormValid ? 0.98 : 1}}
              className={`w-full py-4 px-6 rounded-lg font-bold text-white transition-all flex items-center justify-center gap-2 text-lg ${
                isFormValid && !isSubmitting
                  ? 'bg-gradient-to-r from-green-600 to-yellow-500 hover:from-green-700 hover:to-yellow-600 shadow-lg border-2 border-green-700'
                  : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <span className="mr-1">⚾</span>
                  <SafeIcon icon={FiSend} />
                  Join the Fall 2025 Team!
                </>
              )}
            </motion.button>
          </form>
        </motion.div>

        {/* Info Section with Baseball Theme */}
        <motion.div
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          transition={{duration: 0.6,delay: 0.2}}
          className="mt-8 bg-white rounded-xl shadow-lg p-6 border-2 border-green-200"
        >
          <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <SafeIcon icon={FiInfo} className="text-green-600" />
            <span className="mr-1">⚾</span>
            About Challenger Baseball Fall 2025
          </h3>
          <p className="text-gray-600 leading-relaxed mb-4">
            Challenger Baseball provides children and adults with physical and intellectual challenges the opportunity to play baseball in an organized league. Our volunteers help create a safe, fun, and inclusive environment where everyone can enjoy this wonderful sport.
          </p>

          {/* Location & Schedule */}
          <div className="mt-4 p-4 bg-gradient-to-r from-green-50 to-yellow-50 rounded-lg border-2 border-green-200">
            <div className="flex items-center gap-2 mb-2">
              <SafeIcon icon={FiMapPin} className="text-green-600" />
              <span className="font-semibold text-green-800">Game Location</span>
            </div>
            <p className="text-green-700 font-medium mb-2">
              🏟️ High Road Field
            </p>
            <p className="text-green-600 text-sm mb-3">
              Corner of High Road and Carmel, Fall River
            </p>
            <p className="text-green-800 font-medium">
              📅 Fall 2025 Season - Every Saturday | ⏰ 1:30 - 2:30 PM | ⚾ Game Time!
            </p>
          </div>

          {/* Contact Information */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg border-2 border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">🤝</span>
              <h4 className="font-semibold text-gray-800">Questions or Need Help?</h4>
            </div>
            <div className="space-y-2 text-gray-600">
              <p className="flex items-center gap-2">
                <SafeIcon icon={FiMail} className="text-green-600" />
                <a href="mailto:shawn@shawnryder.com" className="text-green-600 hover:underline font-medium">
                  shawn@shawnryder.com
                </a>
              </p>
              <p className="flex items-center gap-2">
                <SafeIcon icon={FiPhone} className="text-yellow-600" />
                <a href="tel:902-488-4107" className="text-yellow-600 hover:underline font-medium">
                  902-488-4107 (call or text)
                </a>
              </p>
            </div>
          </div>

          {/* Fun Baseball Facts */}
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
            <p className="text-blue-800 text-center font-medium">
              🌟 "Baseball is more than a game - it's about teamwork, inclusion, and making memories!" 🌟
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default App;